import React, { useState } from 'react';
import ApiService from '../../services/api';
import { FundSource } from './types';
import FundSourceModal from './FundSourceModal';

/**
 * AI SUMMARY:
 * The FundSourcesSection component displays and manages fund sources.
 * Features:
 * - Lists all sources connected to a fund
 * - Allows adding new sources via a modal
 * - Provides the ability to remove sources
 * - Handles loading states and error conditions
 * - Refreshes data when sources are added or removed
 */

interface FundSourcesSectionProps {
  fundId: string;
  sources: FundSource[];
  onSourcesChange: () => void;
}

const FundSourcesSection: React.FC<FundSourcesSectionProps> = ({
  fundId,
  sources,
  onSourcesChange,
}) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [removingSourceId, setRemovingSourceId] = useState<string | null>(null);

  const handleRemoveSource = async (sourceId: string) => {
    if (!confirm('Are you sure you want to remove this source?')) {
      return;
    }

    setLoading(true);
    setError(null);
    setRemovingSourceId(sourceId);

    try {
      const token = localStorage.getItem('apiToken');
      if (!token) {
        setError('No API token found');
        setLoading(false);
        return;
      }

      const apiService = new ApiService(token);
      await apiService.removeFundSource(fundId, sourceId);

      // Refresh sources list
      onSourcesChange();
    } catch (error) {
      console.error('Error removing fund source:', error);
      setError('Failed to remove fund source');
    } finally {
      setLoading(false);
      setRemovingSourceId(null);
    }
  };

  const handleSourceAdded = () => {
    onSourcesChange();
  };

  return (
    <div className="bg-white p-6 rounded shadow mb-8">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Fund Sources</h2>
        <button
          onClick={() => setIsModalOpen(true)}
          className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
        >
          Add Source
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {sources.length === 0 ? (
        <p className="text-gray-500">No sources connected to this fund.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Source Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Source ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sources.map((source) => (
                <tr key={source.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {source.sourceType === 'tradier' ? (
                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-medium">
                          Tradier
                        </span>
                      ) : source.sourceType === 'coinbase' ? (
                        <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-medium">
                          Coinbase
                        </span>
                      ) : (
                        <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded text-xs font-medium">
                          {source.sourceType}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {source.sourceId}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-medium">
                      Connected
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleRemoveSource(source.id)}
                      className="text-red-600 hover:text-red-900"
                      disabled={loading && removingSourceId === source.id}
                    >
                      {loading && removingSourceId === source.id
                        ? 'Removing...'
                        : 'Remove'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <FundSourceModal
        fundId={fundId}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSourceAdded={handleSourceAdded}
      />
    </div>
  );
};

export default FundSourcesSection;
